import { apiClient } from "@/api/api-client";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import Combobox from "@/components/core/forms/combobox";
import { type InvoiceRequest, createInvoiceRequestSchema } from "@/features/dashboard/types/invoice-request";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import type { z } from "zod";

interface InvoiceRequestFormProps {
	invoiceRequest?: InvoiceRequest;
	onSubmit: (values: z.infer<typeof createInvoiceRequestSchema>) => Promise<void>;
	isSubmitting?: boolean;
}

const billingTypeOptions = [
	{ value: "one_time", label: "One Time" },
	{ value: "recurring", label: "Recurring" },
	{ value: "mixed", label: "Mixed" },
];

const paymentTermsOptions = [
	{ value: "COD", label: "COD" },
	{ value: "Check / Direct Deposit", label: "Check / Direct Deposit" },
	{ value: "Online Transfer", label: "Online Transfer" },
	{ value: "Net 30 Days", label: "Net 30 Days" },
	{ value: "Others", label: "Others" },
];

const invoiceTypeOptions = [
	{ value: "partial", label: "Partial" },
	{ value: "progress", label: "Progress" },
	{ value: "final", label: "Final" },
];

async function loadUsers(inputValue: string) {
	const response = await apiClient.get("/user-approvals/all", { params: { search: inputValue } });
	const data = response.data;
	const users = data.data || data;
	return users.map((user: { id: string; first_name: string; last_name: string; email: string }) => ({
		value: user.id,
		label: `${user.first_name} ${user.last_name}`.trim() || user.email,
	}));
}

async function loadSalesContracts(inputValue: string) {
	const response = await fetch(`/api/sales-contracts?search=${encodeURIComponent(inputValue)}&per_page=20`);
	const data = await response.json();
	const contracts = data.data || data;
	return contracts.map((contract: { id: string; project_name: string; contract_amount: string; status: string }) => ({
		value: contract.id,
		label: `${contract.project_name} (${Number(contract.contract_amount).toLocaleString()})`,
	}));
}

export function InvoiceRequestForm({ invoiceRequest, onSubmit }: InvoiceRequestFormProps) {
	const form = useForm<z.infer<typeof createInvoiceRequestSchema>>({
		resolver: zodResolver(createInvoiceRequestSchema),
		defaultValues: invoiceRequest
			? {
					requested_by: invoiceRequest.requested_by || "",
					request_date: invoiceRequest.request_date || "",
					client_address: invoiceRequest.client_address,
					tin_no: invoiceRequest.tin_no,
					sales_contract_id: invoiceRequest.sales_contract_id || "",
					invoice_id: invoiceRequest.invoice_id || "",
					is_under_project: invoiceRequest.is_under_project,
					invoice_type: invoiceRequest.invoice_type || undefined,
					billing_type: invoiceRequest.billing_type as "one_time" | "recurring" | "mixed",
					amount_one_time: invoiceRequest.amount_one_time ? Number(invoiceRequest.amount_one_time) : undefined,
					amount_recurring: invoiceRequest.amount_recurring ? Number(invoiceRequest.amount_recurring) : undefined,
					amount_mixed: invoiceRequest.amount_mixed ? Number(invoiceRequest.amount_mixed) : undefined,
					vatable_sales: invoiceRequest.vatable_sales ? Number(invoiceRequest.vatable_sales) : undefined,
					vat_amount: invoiceRequest.vat_amount ? Number(invoiceRequest.vat_amount) : undefined,
					total_amount_due: invoiceRequest.total_amount_due ? Number(invoiceRequest.total_amount_due) : undefined,
					payment_terms: invoiceRequest.payment_terms || "",
					contract_duration_months: invoiceRequest.contract_duration_months || undefined,
					invoice_date_requested: invoiceRequest.invoice_date_requested || "",
					contact_person: invoiceRequest.contact_person,
					contact_number: invoiceRequest.contact_number,
					contact_email: invoiceRequest.contact_email,
					status: invoiceRequest.status as "pending" | "approved" | "processed" | "rejected",
					notes: invoiceRequest.notes || "",
				}
			: {
					requested_by: "",
					request_date: "",
					client_address: "",
					tin_no: "",
					sales_contract_id: "",
					invoice_id: "",
					is_under_project: false,
					invoice_type: undefined,
					billing_type: "one_time",
					amount_one_time: undefined,
					amount_recurring: undefined,
					amount_mixed: undefined,
					vatable_sales: undefined,
					vat_amount: undefined,
					total_amount_due: undefined,
					payment_terms: "",
					contract_duration_months: undefined,
					invoice_date_requested: "",
					contact_person: "",
					contact_number: "",
					contact_email: "",
					status: "pending",
					notes: "",
				},
	});

	const billingType = useWatch({ control: form.control, name: "billing_type" });
	const salesContractId = useWatch({ control: form.control, name: "sales_contract_id" });
	const amountOneTime = useWatch({ control: form.control, name: "amount_one_time" });
	const amountRecurring = useWatch({ control: form.control, name: "amount_recurring" });
	const amountMixed = useWatch({ control: form.control, name: "amount_mixed" });

	const [isVatable, setIsVatable] = useState<boolean>(true);

	// Auto-compute VAT fields based on active amount and contract VAT status
	useEffect(() => {
		let baseAmount = 0;
		if (billingType === "one_time" && amountOneTime) baseAmount = Number(amountOneTime);
		else if (billingType === "recurring" && amountRecurring) baseAmount = Number(amountRecurring);
		else if (billingType === "mixed" && amountMixed) baseAmount = Number(amountMixed);

		if (baseAmount > 0) {
			if (isVatable) {
				const vatable = baseAmount / 1.12;
				const vat = baseAmount - vatable;
				form.setValue("vatable_sales", Math.round(vatable * 100) / 100);
				form.setValue("vat_amount", Math.round(vat * 100) / 100);
			} else {
				form.setValue("vatable_sales", undefined);
				form.setValue("vat_amount", undefined);
			}
			form.setValue("total_amount_due", baseAmount);
		} else {
			form.setValue("vatable_sales", undefined);
			form.setValue("vat_amount", undefined);
			form.setValue("total_amount_due", undefined);
		}
	}, [billingType, amountOneTime, amountRecurring, amountMixed, isVatable, form]);

	type ContractInvoice = {
		id: string;
		invoice_number: string;
		amount: string;
		status: string;
		invoice_date: string;
		due_date: string;
		description: string | null;
		notes: string | null;
		payment_method: string | null;
	};

	type ContractInfo = {
		payment_type: string | null;
		contract_amount: string | null;
		monthly_recurring_sales: string | null;
		start_date: string | null;
		end_date: string | null;
		client_name: string | null;
		client_address: string | null;
		client_email: string | null;
		client_phone: string | null;
		contact_person: string | null;
		contact_email: string | null;
		contact_number: string | null;
		is_vatable: boolean | null;
	};

	const [contractInvoices, setContractInvoices] = useState<ContractInvoice[]>([]);
	const [contractInfo, setContractInfo] = useState<ContractInfo | null>(null);

	const fetchContractInvoices = useCallback(async (contractId: string) => {
		if (!contractId) {
			setContractInvoices([]);
			setContractInfo(null);
			setIsVatable(true);
			form.setValue("invoice_id", "");
			return;
		}
		try {
			const response = await fetch(`/api/invoice-requests/contract-invoices?sales_contract_id=${contractId}`);
			if (response.ok) {
				const data = await response.json();
				setContractInvoices(data.invoices || []);
				setContractInfo(data.contract || null);
				setIsVatable(data.contract?.is_vatable ?? true);

				// Auto-fill contract-level fields
				const contract = data.contract;
				if (contract) {
					if (contract.client_address) {
						form.setValue("client_address", contract.client_address);
					}
					if (contract.contact_person) {
						form.setValue("contact_person", contract.contact_person);
					}
					if (contract.contact_email) {
						form.setValue("contact_email", contract.contact_email);
					}
					if (contract.contact_number) {
						form.setValue("contact_number", contract.contact_number);
					}
					}
			}
		} catch {
			setContractInvoices([]);
			setContractInfo(null);
		}
	}, [form]);

	useEffect(() => {
		fetchContractInvoices(salesContractId || "");
	}, [salesContractId, fetchContractInvoices]);

	const handleInvoiceSelect = useCallback(
		(invoiceId: string | null) => {
			form.setValue("invoice_id", invoiceId || "");

			if (!invoiceId) return;

			const invoice = contractInvoices.find((inv) => inv.id === invoiceId);
			if (!invoice) return;

			// Auto-fill from invoice
			if (invoice.amount) {
				const amount = Number(invoice.amount);
				const billingType = form.getValues("billing_type");
				if (billingType === "one_time") {
					form.setValue("amount_one_time", amount);
				} else if (billingType === "recurring") {
					form.setValue("amount_recurring", amount);
				} else if (billingType === "mixed") {
					form.setValue("amount_mixed", amount);
				}
			}
			if (invoice.invoice_date) {
				form.setValue("invoice_date_requested", invoice.invoice_date.substring(0, 10));
			}
			if (invoice.notes && !form.getValues("notes")) {
				form.setValue("notes", invoice.notes);
			}
		},
		[contractInvoices, form],
	);

	return (
		<Form {...form}>
			<form id="invoice-request-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-6">
					{/* Row 1: Request Date | Requestor */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="request_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Request Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="requested_by"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Requestor</FormLabel>
										<FormGroup>
											<Combobox
												placeholder="Select requestor"
												loadOptions={loadUsers}
												defaultOptions
												clearable
												onValueChange={(value) => field.onChange(value)}
												defaultValue={field.value}
												placeholderValue={
													invoiceRequest?.requested_by_user
														? {
																value: invoiceRequest.requested_by_user.id,
																label: invoiceRequest.requested_by_user.name,
															}
														: undefined
												}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Row 2: Sales Contract | TIN No. */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="sales_contract_id"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Sales Contract</FormLabel>
										<FormGroup>
											<Combobox
												placeholder="Search and select sales contract"
												loadOptions={loadSalesContracts}
												defaultOptions
												clearable
												onValueChange={(value) => field.onChange(value)}
												defaultValue={field.value}
												placeholderValue={
													invoiceRequest?.sales_contract
														? {
																value: invoiceRequest.sales_contract.id,
																label: `${invoiceRequest.sales_contract.project_name} (${Number(invoiceRequest.sales_contract.contract_amount).toLocaleString()})`,
															}
														: undefined
												}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="tin_no"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>TIN No.</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter TIN number" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Invoice Select - shown when sales contract is selected */}
					{salesContractId && (
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-6">
								<FormField
									control={form.control}
									name="invoice_id"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Invoice</FormLabel>
											<FormGroup>
												<Select
													placeholder={contractInvoices.length === 0 ? "No invoices available" : "Select invoice"}
													options={contractInvoices.map((inv) => ({
														value: inv.id,
														label: `${inv.invoice_number} - ${inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : "No date"} - ${Number(inv.amount).toLocaleString()} (${inv.status})`,
													}))}
													defaultValue={field.value || undefined}
													onValueChange={(value) => handleInvoiceSelect(value)}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					)}

					{/* Row 3: Complete Address */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="client_address"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Complete Address</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter complete address" rows={2} {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Row 5: Invoice Type | Billing Type */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="invoice_type"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Invoice Type</FormLabel>
										<FormGroup>
											<Select
												placeholder="Select invoice type"
												options={invoiceTypeOptions}
												defaultValue={field.value || undefined}
												onValueChange={field.onChange}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="billing_type"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Billing Type</FormLabel>
										<FormGroup>
											<Select
												placeholder="Select billing type"
												options={billingTypeOptions}
												defaultValue={field.value}
												onValueChange={field.onChange}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Row 6: Amount fields - conditional on billing type */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						{(billingType === "one_time" || billingType === "mixed") && (
							<div className="col-span-4">
								<FormField
									control={form.control}
									name="amount_one_time"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Amount (One Time)</FormLabel>
											<FormGroup>
												<Input
													className="input"
													type="number"
													step="0.01"
													placeholder="0.00"
													{...field}
													value={field.value ?? ""}
													onChange={(e) => field.onChange(e.target.value ? Number.parseFloat(e.target.value) : undefined)}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						)}
						{(billingType === "recurring" || billingType === "mixed") && (
							<div className="col-span-4">
								<FormField
									control={form.control}
									name="amount_recurring"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Amount (Recurring)</FormLabel>
											<FormGroup>
												<Input
													className="input"
													type="number"
													step="0.01"
													placeholder="0.00"
													{...field}
													value={field.value ?? ""}
													onChange={(e) => field.onChange(e.target.value ? Number.parseFloat(e.target.value) : undefined)}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						)}
						{billingType === "mixed" && (
							<div className="col-span-4">
								<FormField
									control={form.control}
									name="amount_mixed"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Amount (Mixed)</FormLabel>
											<FormGroup>
												<Input
													className="input"
													type="number"
													step="0.01"
													placeholder="0.00"
													{...field}
													value={field.value ?? ""}
													onChange={(e) => field.onChange(e.target.value ? Number.parseFloat(e.target.value) : undefined)}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						)}
					</div>

					{/* VAT Breakdown */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="vatable_sales"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Vatable Sales</FormLabel>
										<FormGroup>
											<Input
												className="input"
												type="number"
												step="0.01"
												placeholder="0.00"
												disabled={!isVatable}
												{...field}
												value={field.value ?? ""}
												onChange={(e) => field.onChange(e.target.value ? Number.parseFloat(e.target.value) : undefined)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="vat_amount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>VAT (12%)</FormLabel>
										<FormGroup>
											<Input
												className="input"
												type="number"
												step="0.01"
												placeholder="0.00"
												disabled={!isVatable}
												{...field}
												value={field.value ?? ""}
												onChange={(e) => field.onChange(e.target.value ? Number.parseFloat(e.target.value) : undefined)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="total_amount_due"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Total Amount Due</FormLabel>
										<FormGroup>
											<Input
												className="input"
												type="number"
												step="0.01"
												placeholder="0.00"
												{...field}
												value={field.value ?? ""}
												onChange={(e) => field.onChange(e.target.value ? Number.parseFloat(e.target.value) : undefined)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Row 7: Payment Terms | Contract Duration */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="payment_terms"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Payment Terms</FormLabel>
										<FormGroup>
											<Select
												placeholder="Select payment terms"
												options={paymentTermsOptions}
												defaultValue={field.value || undefined}
												onValueChange={field.onChange}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="contract_duration_months"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Contract Duration (Months)</FormLabel>
										<FormGroup>
											<Input
												className="input"
												type="number"
												placeholder="Enter duration in months"
												{...field}
												value={field.value ?? ""}
												onChange={(e) => field.onChange(e.target.value ? Number.parseInt(e.target.value) : undefined)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Row 8: Invoice Date Requested */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="invoice_date_requested"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Invoice Date Requested</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Row 9: Contact Person | Contact Number | Contact Email */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="contact_person"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Contact Person</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter contact person" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="contact_number"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Contact Number</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter contact number" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="contact_email"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Contact Email</FormLabel>
										<FormGroup>
											<Input className="input" type="email" placeholder="Enter contact email" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Row 10: Notes / Remarks */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="notes"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Notes / Remarks</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter notes or remarks" rows={3} {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
